import type { ReactNode } from 'react'
import { Seo } from '@saas-pages/shared/public/seo'
import { LocalizedProcessingBadge, useLocale, useT } from '@/shared/i18n'
import { seoFor } from '@/seo/routes'
import { buildUtilities, type UtilityKey } from '@/shared/utilities/utilities'
import { ToolBreadcrumb } from './ToolBreadcrumb'
import { ToolLongForm } from './ToolLongForm'
import { ToolHowTo } from './ToolHowTo'
import { ToolFaq } from './ToolFaq'
import { ToolCrossPromo } from './ToolCrossPromo'

interface ToolPageShellProps {
  utilityKey: UtilityKey
  children: ReactNode
}

export function ToolPageShell({
  utilityKey,
  children,
}: ToolPageShellProps) {
  const t = useT()
  const { locale } = useLocale()
  const utility = buildUtilities(t).find((u) => u.key === utilityKey)
  if (!utility) {
    throw new Error(`[ToolPageShell] Unknown utilityKey: "${utilityKey}"`)
  }

  const route = seoFor(utilityKey, locale)
  return (
    <>
      <Seo metadata={route.metadata} />

      <div className="mx-auto max-w-5xl">
        <div className="mx-auto max-w-3xl">
          <ToolBreadcrumb
            items={[
              { label: 'pdfy.ru', to: '/' },
              { label: utility.title },
            ]}
          />

          <div className="mb-4 sm:mb-8">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-bold tracking-tight sm:text-4xl">
                {utility.title}
              </h1>
              <LocalizedProcessingBadge />
            </div>
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground sm:mt-3 sm:line-clamp-none sm:text-base">
              {utility.shortDescription}
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-4xl">
          {children}
        </div>

        <div className="mx-auto max-w-3xl">
          <ToolLongForm paragraphs={utility.marketingParagraphs} />

          {utility.howTo.length > 0 && (
            <ToolHowTo steps={utility.howTo} heading={t.tool.howToHeading} />
          )}

          {utility.faq.length > 0 && (
            <ToolFaq items={utility.faq} heading={t.tool.faqHeading} />
          )}

          <ToolCrossPromo current={utilityKey} />
        </div>
      </div>
    </>
  )
}
